import { colors } from "@indeed/ifl-tokens";
import React, { useState } from "react";
import { StyleSheet } from "react-native";

import {
  Box,
  Button,
  ChevronDown,
  ChevronUp,
  Divider,
  Heading,
  Text,
} from "../../../../../../mads";
import { useI18n } from "../../../../../../shared/I18n";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";

/**
 * Used to render a list of benefits for this job.
 *
 * If the list is larger than a threshold, they are collapsed and can be shown by clicking a show more chevron.
 */
export const Benefits = () => {
  const viewjobOneGraphModel = useViewjobOneGraphModel();

  const benefits = viewjobOneGraphModel?.benefits;
  const i18n = useI18n();

  const [isExpanded, setIsExpanded] = useState(false);

  if (!benefits || benefits.benefits.length === 0) {
    return null;
  }

  const { benefits: benefitsList, benefitsCollapseThreshold: expandThreshold } = benefits;

  const canExpand = benefitsList.length > expandThreshold;

  const displayedBenefits =
    canExpand && !isExpanded ? benefitsList.slice(0, expandThreshold) : benefitsList;

  const benefitsHeader = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 8,
      }}
    >
      <Heading level={4}>{i18n.pgettext("Section header", "Benefits")}</Heading>
      <Text
        level={2}
        sx={{
          color: colors.neutral["700"],
          fontWeight: "400",
        }}
      >
        {i18n.pgettext("Caption", "Pulled from the full job description")}
      </Text>
    </Box>
  );

  const benefitsContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        alignItems: "flex-start",
      }}
    >
      {displayedBenefits.map((item) => (
        <Box key={item.key} sx={{ display: "flex", flexDirection: "row" }}>
          <Box sx={{ paddingRight: 8 }}>
            <Box sx={styles.bulletPoint} />
          </Box>
          <Text
            level={3}
            key={item.key}
            sx={{
              color: colors.neutral["800"],
            }}
          >
            {item.label}
          </Text>
        </Box>
      ))}
      {canExpand && (
        <Button
          testID="collapsedBenefitsButton"
          variant="text"
          iconAfter={
            isExpanded ? (
              <ChevronUp
                size="md"
                color={colors.primary["800"]}
                title={i18n.gettext("chevron up")}
                aria-hidden
              />
            ) : (
              <ChevronDown
                size="md"
                color={colors.primary["800"]}
                title={i18n.gettext("chevron down")}
                aria-hidden
              />
            )
          }
          sx={{
            paddingLeft: 0,
            paddingTop: 0,
          }}
          onPress={() => setIsExpanded(!isExpanded)}
        >
          {!isExpanded
            ? i18n.pgettext("CTA to show more benefits", "Show more")
            : i18n.pgettext("CTA to show fewer benefits", "Show less")}
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ marginVertical: 24 }}>
        <Box
          sx={[
            {
              paddingLeft: 4,
              paddingRight: 4,
            },
          ]}
        >
          {benefitsHeader}
          {benefitsContent}
        </Box>
      </Box>
      <Divider />
    </>
  );
};

const styles = StyleSheet.create({
  bulletPoint: {
    height: 5,
    width: 5,
    backgroundColor: colors.neutral["800"],
    borderRadius: 20,
    marginTop: 10,
    marginRight: 5,
  },
  blur: {
    height: 120,
    width: "100%",
    position: "absolute",
    bottom: 0,
  },
});
